import { useEffect, useState } from "react";
import { FaCheckCircle, FaRegCircle } from "react-icons/fa";
import { MdChangeCircle, MdInfoOutline, MdInfo } from "react-icons/md";
import { Goal } from "interfaces/Goal";
import { transformCurrency } from "utils/functions/currencyModUtil";
import { getOfferSaleMultiplierLabel } from "utils/functions/offerSaleUtil";
import * as c from "./RewardModalGoalTask.module.css";

interface RewardModalGoalTaskProps {
  goal: Goal;
  currencyName: string;
  index: number;
  daysRemaining: number;
  hoursRemaining: number;
  indexZeroTitle: string | undefined;
}

const RewardModalGoalTask = ({
  goal,
  currencyName,
  index,
  daysRemaining,
  hoursRemaining,
  indexZeroTitle,
}: RewardModalGoalTaskProps) => {
  const { id, title, description, completeByUtc, rewardIac, isCompleted, isPending } = goal;
  const currency = transformCurrency(currencyName, rewardIac);
  const originalCurrency =
    goal.promotion == null
      ? undefined
      : transformCurrency(currencyName, goal.promotion?.originalRewardIac!);
  const [completedTooltipVisible, setCompletedTooltipVisible] = useState(false);
  const [titleTooltipVisible, setTitleTooltipVisible] = useState(false);
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);
  const [remainingTimeInfoOpen, setRemainingTimeInfoOpen] = useState(false);

  const isPromotion = () => {
    return goal.promotion !== null && goal.promotion?.originalRewardIac !== goal.rewardIac;
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (completedTooltipVisible) {
      timer = setTimeout(() => {
        setCompletedTooltipVisible(false);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [completedTooltipVisible]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (titleTooltipVisible) {
      timer = setTimeout(() => {
        setTitleTooltipVisible(false);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [titleTooltipVisible]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (infoTooltipVisible) {
      timer = setTimeout(() => {
        setInfoTooltipVisible(false);
      }, 6000);
    }
    return () => clearTimeout(timer);
  }, [infoTooltipVisible]);

  const remainingTimeInfoHandler = () => {
    setRemainingTimeInfoOpen(!remainingTimeInfoOpen);
  };

  const getRemainingTime = (completeByUtc: string): string => {
    if (!completeByUtc) return "Expired";

    const completeBy = new Date(
      completeByUtc.endsWith("Z") ? completeByUtc : completeByUtc + "Z"
    );
    const now = new Date(Date.now());


    const diffMs = completeBy.getTime() - now.getTime();

    if (diffMs <= 0) return "Expired";

    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes >= 1440) {
      const days = Math.floor(diffMinutes / 1440);
      return `${days} day${days > 1 ? "s" : ""}`;
    } else if (diffMinutes >= 60) {
      const hours = Math.floor(diffMinutes / 60);
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    } else {
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
    }
  };

  return (
    <div className={c.task}>
      <div
        className={`${c["task-content-container"]} ${isCompleted && c["task-content-completed"]} ${
          isPending && c["task-content-pending"]
        }`}
      >
        <div className={c["content-wrapper-top"]}>
          {isCompleted ? (
            <FaCheckCircle color={"#249a63"} size={"20px"} />
          ) : isPending ? (
            <MdChangeCircle color={"#ffb700"} size={"22px"} />
          ) : (
            <FaRegCircle color={"#94a3b8"} size={"20px"} />
          )}
          <div className={c["task-text-wrapper"]}>
            <span className={c["text-font"]}>{title}</span>
            <div className={c["task-text"]}>
              <div className={c["task-text-pending-top"]}>
                {isPromotion() ? (
                  <div className={c["task-text-boost"]}>
                    <span className={c["task-currency-line"]}>{originalCurrency}</span>
                    <span className={c["task-currency-boost"]}>{currency}</span>
                  </div>
                ) : (
                  <span className={c["task-currency"]}>{currency}</span>
                )}
                {isPending && <span className={c["task-pending-label"]}>In-Progress</span>}
                {isPromotion() && (
                  <span
                    className={`${c["task-boost-label"]} ${
                      isPending ? c["boost-label-with-pending"] : ""
                    }`}
                  >
                    {getOfferSaleMultiplierLabel(goal.promotion?.multiplier!)}
                  </span>
                )}
              </div>
              {isPending && (
                <div className={c["task-text-pending-bottom"]}>
                  <span>Please allow up to 7 days to credit</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {completeByUtc && (
          <div
            className={`${c["remaining-time-info-container"]} ${
              remainingTimeInfoOpen && c["open"]
            }`}
            onClick={remainingTimeInfoHandler}
          >
            {getRemainingTime(completeByUtc) !== "Expired" ? (
              <div className={c["remaining-time-info"]}>
                {remainingTimeInfoOpen ? (
                  <MdInfo color={"white"} size={"18px"} />
                ) : (
                  <MdInfoOutline color={"white"} size={"18px"} />
                )}
                <span className={c["time-info-date"]}>{getRemainingTime(completeByUtc)}</span>{" "}
                <span className={c["time-info-text"]}> remaining to complete</span>
              </div>
            ) : (
              <div className={c["remaining-time-info-expired"]}>
                <span>
                  You are no longer eligible for this goal, time window to complete expired.
                </span>
              </div>
            )}
            {remainingTimeInfoOpen && getRemainingTime(completeByUtc) !== "Expired" && (
              <div className={c["remaining-time-info-wrapper"]}>
                <span>
                  {`To receive the reward of ${currency} for this goal, you must have this goal, “${title}” fully completed within ${getRemainingTime(
                    completeByUtc,
                  )} from now. No reward will be be given
                if you complete this goal longer than ${getRemainingTime(completeByUtc)} from now.`}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardModalGoalTask;
